'use client';

import type { ModalProps } from '@highlight-cp/core-ui';
import { Modal } from '@highlight-cp/core-ui';

import type { ModalButtonsProps } from '../modal-buttons';
import { ModalButtons } from '../modal-buttons';

/** The footer half of {@link ActionModalProps}; everything else passes through to `Modal`. */
type ActionModalFooterProps = Pick<
  ModalButtonsProps,
  | 'cancelDisabled'
  | 'cancelText'
  | 'cancelVariant'
  | 'confirmAutoFocus'
  | 'confirmDisabled'
  | 'confirmText'
  | 'leftSection'
  | 'loading'
  | 'onCancel'
  | 'onConfirm'
  | 'withCancelButton'
  | 'withConfirmButton'
>;

export interface ActionModalProps
  extends Omit<ModalProps, keyof ActionModalFooterProps>, ActionModalFooterProps {}

/**
 * ### ActionModal
 *
 * @description
 * core-ui's `Modal` with a Cancel/Save row in its fixed footer. Use it for a modal that *does*
 * something — a form to submit, a selection to apply — where the body needs live React state.
 *
 * For a plain "are you sure?" prompt fired from an event handler, use `useConfirmModal` /
 * `useConfirmDeleteModal` from `@highlight-cp/core-ui` instead: those open imperatively and take a
 * static message. For a modal with no footer at all, use `Modal` directly.
 *
 * `onConfirm` does not close the modal — the caller owns `opened`, so close in the handler once the
 * work succeeds. Cancel runs `onClose` unless `onCancel` gives it a job of its own, such as
 * discarding a draft while the modal stays open. `leftSection` puts an action of its own at the
 * start of the row, such as removing what the modal edits. To ask before that removal without
 * stacking a second modal, pass `useConfirmDeleteBanner`'s `banner`.
 *
 * @example
 * ```tsx
 * <ActionModal
 *   opened={isOpen}
 *   onClose={close}
 *   title={t('Rename Project')}
 *   confirmText={t('Rename')}
 *   confirmDisabled={!name}
 *   loading={isSaving}
 *   onConfirm={handleRename}
 * >
 *   <TextInput label={t('Name')} {...form.getInputProps('name')} />
 * </ActionModal>
 * ```
 *
 * @param props - {@link ActionModalProps}
 * @returns React component
 */
export const ActionModal = (props: ActionModalProps) => {
  const {
    cancelDisabled,
    cancelText,
    cancelVariant,
    children,
    confirmAutoFocus,
    confirmDisabled,
    confirmText,
    leftSection,
    loading,
    onCancel,
    onClose,
    onConfirm,
    withCancelButton,
    withConfirmButton,
    ...modalProps
  } = props;

  return (
    <Modal onClose={onClose} {...modalProps}>
      {children}

      <ModalButtons
        cancelDisabled={cancelDisabled}
        cancelText={cancelText}
        cancelVariant={cancelVariant}
        confirmAutoFocus={confirmAutoFocus}
        confirmDisabled={confirmDisabled}
        confirmText={confirmText}
        leftSection={leftSection}
        loading={loading}
        onCancel={onCancel}
        onClose={onClose}
        onConfirm={onConfirm}
        withCancelButton={withCancelButton}
        withConfirmButton={withConfirmButton}
      />
    </Modal>
  );
};
